import abbreviations from './abbreviations.json';
import { topicsOf, TOPICS } from '../data/educationTaxonomy';

/*
 * Education Center search: one function over the loaded catalogue, with the facets the
 * results screen offers. Ranked so a title hit beats a tag hit beats a description hit.
 *
 *   facets = { types: ['video'], levels: ['CA-1'], topic: 'cardiac', featured: false, recent: false, saved: false }
 */

export const EMPTY_FACETS = { types: [], levels: [], topic: null, featured: false, recent: false, saved: false, shelf: false, kind: null };

export const facetsActive = (f) => Boolean(f.types.length || f.levels.length || f.topic || f.featured || f.recent || f.saved || f.shelf || f.kind);

export const facetCount = (f) => f.types.length + f.levels.length + (f.topic ? 1 : 0) + (f.featured ? 1 : 0) + (f.recent ? 1 : 0) + (f.saved ? 1 : 0) + (f.shelf ? 1 : 0) + (f.kind ? 1 : 0);

/** "CPB" also searches "cardiopulmonary bypass", and the reverse */
export const expandTerms = (q) => {
  const raw = q.trim();
  if (!raw) return [];
  const terms = [raw];
  const upper = raw.toUpperCase();
  if (abbreviations[upper]) terms.push(...abbreviations[upper]);
  const lower = raw.toLowerCase();
  Object.entries(abbreviations).forEach(([abbr, fulls]) => {
    if (fulls.some((t) => t.toLowerCase().includes(lower))) terms.push(abbr, ...fulls);
  });
  return [...new Set(terms.map((t) => t.toLowerCase()))];
};

export const toMillis = (d) => (d && typeof d.toMillis === 'function' ? d.toMillis() : (d ? new Date(d).getTime() : 0));
const MONTH = 30 * 24 * 60 * 60 * 1000;

const score = (item, terms) => {
  let best = 0;
  const title = (item.title || '').toLowerCase();
  const tags = (item.tags || []).map((t) => t.toLowerCase());
  const desc = (item.description || '').toLowerCase();
  const topicText = topicsOf(item).map((t) => t.label.toLowerCase()).join(' ') + ' ' + (item.subcategory || '').toLowerCase();
  const presenter = (item.presenter || item.uploadedBy || '').toLowerCase();
  const series = (item.series || '').toLowerCase();
  terms.forEach((t) => {
    if (title.startsWith(t)) best = Math.max(best, 100);
    else if (title.includes(t)) best = Math.max(best, 80);
    if (tags.some((g) => g.includes(t))) best = Math.max(best, 60);
    if (series.includes(t)) best = Math.max(best, 50);
    if (topicText.includes(t)) best = Math.max(best, 40);
    if (presenter.includes(t)) best = Math.max(best, 35);
    if (desc.includes(t)) best = Math.max(best, 20);
  });
  return best;
};

export const matchesFacets = (item, f, savedIds, shelfIds) => {
  if (f.shelf && !(shelfIds && shelfIds.has(item.id))) return false;
  if (f.types.length && !f.types.includes(item.fileType)) return false;
  if (f.levels.length && !f.levels.includes(item.trainingLevel || 'All Levels')) return false;
  if (f.topic && !topicsOf(item).some((t) => t.id === f.topic)) return false;
  if (f.featured && !item.featured) return false;
  if (f.recent && toMillis(item.uploadDate) < Date.now() - MONTH) return false;
  if (f.saved && !(savedIds && savedIds.has(item.id))) return false;
  return true;
};

/** Items matching the query and facets, best match first; with no query, newest first */
export const searchItems = (items, q, f = EMPTY_FACETS, savedIds, shelfOrder) => {
  if (f.kind) return []; // a collection kind facet lists collections only
  const terms = expandTerms(q);
  const shelfIds = shelfOrder ? new Set(shelfOrder) : null;
  const pool = items.filter((it) => matchesFacets(it, f, savedIds, shelfIds));
  if (!terms.length) {
    // The shelf keeps its own order (continue, saved, recent); everything else is newest first
    if (f.shelf && shelfOrder) {
      const rank = new Map(shelfOrder.map((id, i) => [id, i]));
      return pool.sort((a, b) => rank.get(a.id) - rank.get(b.id));
    }
    return pool.sort((a, b) => toMillis(b.uploadDate) - toMillis(a.uploadDate));
  }
  return pool
    .map((it) => ({ it, s: score(it, terms) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || toMillis(b.it.uploadDate) - toMillis(a.it.uploadDate))
    .map((x) => x.it);
};

export const searchCollections = (collections, q, f = EMPTY_FACETS) => {
  const terms = expandTerms(q);
  return collections.filter((c) => {
    if (f.kind && c.kind !== f.kind) return false;
    if (f.topic && !(c.topics || []).includes(f.topic)) return false;
    if (f.levels.length && !f.levels.includes(c.level || 'All Levels')) return false;
    if (f.types.length || f.featured || f.recent || f.saved || f.shelf) return false; // item-only facets
    if (!terms.length) return true;
    const text = `${c.title} ${c.description || ''}`.toLowerCase();
    return terms.some((t) => text.includes(t));
  });
};

/** Topics whose name or subtopics match the query: the "did you mean" for an empty result */
export const suggestTopics = (q) => {
  const terms = expandTerms(q);
  if (!terms.length) return [];
  return TOPICS.filter((t) => {
    const text = `${t.label} ${t.description} ${t.subtopics.join(' ')}`.toLowerCase();
    return terms.some((x) => text.includes(x));
  }).slice(0, 4);
};

/** Distinct training levels present in the catalogue, in curriculum order */
const LEVEL_ORDER = ['Medical Student', 'CA-1', 'CA-2', 'CA-3', 'Fellow', 'Faculty', 'All Levels'];
export const levelsIn = (items) => {
  const present = new Set(items.map((i) => i.trainingLevel).filter((l) => l && l !== 'All Levels'));
  return LEVEL_ORDER.filter((l) => present.has(l));
};

export const groupByKind = (items) => ({
  video: items.filter((i) => i.fileType === 'video'),
  presentation: items.filter((i) => i.fileType === 'presentation'),
  document: items.filter((i) => i.fileType === 'document'),
});
